import * as Yup from 'yup';
import {  ActionsEnum, BlankEnum, CleavageEnum, CryoMediaEnum, CryoPurposeEnum, CultureDishConfigEnum, EmbryoFinilizeUpdateType,NullEnum,  StatusDa2Enum } from './embryoCulture.helpers';
import { getRequest, putRequest } from '../../../services/httpService';

// Define the initial embryo culture and update details types
export interface EmbryoCultureType {
    time: string; 
    embryologist?: number | null; 
    witness?: number | null; 
    incubator?: string | null; 
    remarks?: string | null; 
    culture_dish_config?: string | BlankEnum | NullEnum | null; 
    day: number; 
    cycle: number;
    id?: number | null | undefined;
    is_assisted_hatching: boolean;
  }

export interface OocyteFile {
    embryo_no: number;
    file: string; // URL of the file
  }  

export interface EmbryoUpdateDetailsType {    
    cycle: number;
    embryos: number[] | null;
    stage: string | null;
    grade: string | null;
    cleavage: string | null;
    action: string | BlankEnum | null;
    day: number | null;
    status: string | null;
    pgt_order_date:string | null;
    pgt_order_time:string | null;
    pgt_order_biopsy_cells:string | null;
    pgt_type:string | null;
    pgt_facility:string | null;
    pgt_result_embryo_sex:string | null;
    pgt_results:string | null;
    pgt_result_moisture:string | null;
    pgt_result_notes:string | null;

    symmetry :string | null; 
    
    cryo_preserved_date?: string | null;
    cryo_preserved_by?: number | null;
    cryo_purpose?: string | null;
    cryo_media?: string | null;
    is_screened?: boolean | null;
    
    source?: number | null;
    thaw_cycle?: number | null;
    locations?: number | null;
    files?: OocyteFile[];
  }
  
// Define initial values for single object
export const initialEmbryoCultureValues: EmbryoCultureType = {
  embryologist: null,
  witness: null,
  cycle: 0,
  remarks: '',
  culture_dish_config: '',
  day: 0,
  time: '',
  incubator: '',
  is_assisted_hatching: false, 
};
  
export const initialEmbryoUpdateValues: EmbryoUpdateDetailsType = {
  cycle: 55,
  embryos: [],
  action: null,
  stage: null,
  grade: null,
  cleavage: null,
  day: 4,
  status: 'DRAFT',
  pgt_order_date: null,
  pgt_order_time: null,
  pgt_order_biopsy_cells: null,
  pgt_type: null,
  pgt_facility: null,
  
  pgt_result_embryo_sex: null,
  pgt_results: null,
  pgt_result_moisture: null,
  pgt_result_notes: null,  

  symmetry:null, 
  
  cryo_preserved_date: null,
  cryo_preserved_by: null,
  cryo_purpose: null,
  cryo_media: null,
  is_screened: false,
  
};
  
  
// Validation Schema for single embryo culture
export const EmbryoCultureValidationSchema = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => Yup.object().shape({

  embryologist: Yup.number()
    .required(tGeneral('embryologistNameIsRequired')),
  witness: Yup.number()
    .required(tGeneral('witnessEmbryologistNameIsRequired'))
    .test('not-same', tGeneral('witnessEmbryologistCannotBeSame'), function (value) {
      const { embryologist } = this.parent; // Accessing the sibling field
      return value !== embryologist; // Validate that they are not the same
    }),
  
  cycle: Yup.number().integer(),
  remarks: Yup.string().nullable(),
  culture_dish_config: Yup.mixed<CultureDishConfigEnum | BlankEnum | NullEnum>()
    .oneOf([
      'NORMAL_SINGLE_CULTURE', 'EMBRYO_SLIDE_TIMELAPSE_SINGLE_CULTURE', 
      'CULTURE_PRO_TIMELAPSE_SINGLE_CULTURE', 'EMBRYO_SLIDE_IC8_DISH_TIMELAPSE_SINGLE_CULTURE', 
      '38_SPECIAL_GPS_SINGLE_CULTURE', 'EMBRYO_GPS_SINGLE_CULTURE', 
      'MINI_GPS_GPS_SINGLE_CULTURE', '4_WELL_GPS_SINGLE_CULTURE', 
      'U_DROP_GPS_SINGLE_CULTURE', 'EMBRYO_CORRAL_GPS_SINGLE_CULTURE', 
      'NORMAL_GROUP_CULTURE', 'MICROWELL_GROUP_CULTURE', '', 'NULL'
    ])
    .nullable(),
  day: Yup.number().min(0).max(30).integer(),
  time: Yup.string().required(),
  incubator: Yup.string().nullable(),
    
  is_assisted_hatching: Yup.boolean().nullable(), 
});
  
// Validation Schema for single embryo update details
export const EmbryoValidationSchema = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => Yup.object().shape({
  cycle: Yup.number().positive(tGeneral('cycleMustBeAPositiveNumber')),
  embryos: Yup.array()
    .required(tGeneral('embryoNumberIsRequired')),
  action: Yup.mixed<ActionsEnum | BlankEnum>()
    .oneOf(['CULTURE', 'FREEZE', 'DISCARD', 'TRANSFER'], 'Invalid action')
    .required(),
  stage: Yup.string().nullable(),
  grade: Yup.string().nullable(),
  cleavage: Yup.mixed<CleavageEnum>()
    .nullable()
    .oneOf(['INTACT', 'SYNGAMY', 'EARLY_CLEAVAGE'], 'Invalid cleavage value'),
  day: Yup.number().positive('Day must be a positive number'),
  status: Yup.mixed<StatusDa2Enum>()
    .oneOf(['DRAFT', 'FINALIZED'], 'Invalid status').nullable(),
  cryo_preserved_date: Yup.string().nullable(),
  cryo_preserved_by: Yup.string().nullable(),
  cryo_purpose: Yup.mixed<CryoPurposeEnum>()
    .nullable()
    .oneOf(
      [
        'PRESERVATION_OF_EXCESS_EMBRYOS', 
        'TIMED_EMBRYO_TRANSFER', 
        'FERTILITY_PRESERVATION', 
        'GENETIC_TESTING', 
        'FLEXIBLE_FAMILY_PLANNING', 
        'REDUCING_MULTIPLE_PREGNANCIES',
      ], 
      'Invalid cryo purpose'
    ),
  cryo_media: Yup.mixed<CryoMediaEnum>()
    .nullable()
    .oneOf(['KITAZATO', 'SWEMED', 'IRVINE_SCIENTIFIC'], 'Invalid cryo media'),
  is_screened: Yup.boolean(),
});
  
// Combined Validation Schema for single objects
export const CombinedValidationSchema = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => Yup.object().shape({
  embryoCulture: EmbryoCultureValidationSchema(tGeneral),
  embryoDetails: EmbryoValidationSchema(tGeneral),
});


// Function to update embryo details
export function updateEmbryoSummary(id: number, data: EmbryoCultureType | EmbryoFinilizeUpdateType): Promise<EmbryoCultureType | EmbryoFinilizeUpdateType> {
  const url = `/embryo/cycle/${id}/`;
  return putRequest(url, data, { responseType: 'json' });
}


// Function to get Embryo retrieval
export function getEmbryoDetails(id: number): Promise<EmbryoCultureType> {
  const url =`oocyte-details/${id}`;
  return getRequest(url, { responseType: 'json' });
}

// Function to get Embryo Details
// export function getEmbryoCultureDetails(id: number,  day: number): Promise<any> {
//   const url =`cycle/${id}/embryos/?day=${day}`;
//   return getRequest(url, { responseType: 'json' });
// }


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getEmbryoCultureDetails(id: number, day: number, source?: number, thawId?:number | null): Promise<any> {
  let url = `cycle/${id}/embryos/?day=${day}`;

  // Only append the source parameter if it is provided
  if (source !== undefined && source !== null) {
    url += `&source=${source}`;
  }

  if (thawId !== undefined && thawId !== null) {
    url += `&thaw_cycle=${thawId}`;
  }
  return getRequest(url, { responseType: 'json' });
}


  
// Function to update Embryo details
export function updateEmbryoDetails(id: number, data: EmbryoCultureType): Promise<EmbryoCultureType> {
  const url = `/embryo-culture/${id}/details/`;
  return putRequest(url, data, { responseType: 'json' });
}

// Function to update Embryo Summary details
export function updateEmbryoSummaryDetails(cycle_id : number, data: EmbryoUpdateDetailsType | EmbryoFinilizeUpdateType): Promise<EmbryoUpdateDetailsType> {
  const url = `/embryo/cycle/${cycle_id}/`;
  return putRequest(url, data, { responseType: 'json' });
}


export const getColumns = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => [
  { label: tGeneral('embryoNo', { defaultValue: 'Embryo No.' }), field: 'embryoNo', editable: false },
  { label: tGeneral('image', { defaultValue: 'Image' }), field: 'image', editable: false },
  { label: tGeneral('stage', { defaultValue: 'Stage' }), field: 'stage', editable: false },
  { label: tGeneral('grade', { defaultValue: 'Grade' }), field: 'grade', editable: false },
  { label: tGeneral('aiScore', { defaultValue: 'AI Score' }), field: 'ai_score', editable: false },
  { label: tGeneral('pgtOrderDetailsTitle', { defaultValue: 'PGT Order Details' }), field: 'pgt_order', editable: false },
  { label: tGeneral('PGTResult', { defaultValue: 'PGT Result' }), field: 'pgt_results', editable: false },
  { label: tGeneral('action', { defaultValue: 'Action' }), field: 'action', editable: false },
  { label: tGeneral('freezeLocation', { defaultValue: 'Freeze Location' }), field: 'location_details', editable: true },
];

export const rows = [];